import { useCallback, useLayoutEffect, useRef, useState, type RefObject } from "react";
import { isLfMiniProgramEnv } from "./miniProgramEnv";
import {
  calcMiniProgramNativeNavClearancePadPx,
  measureCssEnvSafeAreaTopPx,
  measureElementPhysicalTopPx,
} from "./viewportFill";

/** 键盘弹起时 WKWebView / 小程序常把 visualViewport 上移 */
function isVisualViewportPanned(): boolean {
  return (window.visualViewport?.offsetTop ?? 0) > 1;
}

/**
 * 顶部补偿 padding（px）：
 * - 小程序（微信/抖音）：若元素顶相对物理屏顶 < 原生标题栏底，则补齐到原生标题栏底；
 * - 非小程序：相对 env(safe-area-inset-top) 补齐。
 */
export function calcSafeAreaTopPadPx(el: Element | null): number {
  if (!el) {
    return 0;
  }
  if (isLfMiniProgramEnv()) {
    return calcMiniProgramNativeNavClearancePadPx(el);
  }
  const safeTop = measureCssEnvSafeAreaTopPx();
  if (safeTop <= 0) {
    return 0;
  }
  const top = measureElementPhysicalTopPx(el);
  if (top >= safeTop) {
    return 0;
  }
  return Math.ceil(safeTop - top);
}

/** 在 mount / resize / visualViewport scroll 时测量 ref 元素，返回需额外叠加的 padding-top（px） */
export function useSafeAreaTopPad(
  ref: RefObject<HTMLElement | null>,
  options?: { pauseWhile?: () => boolean }
): number {
  const [pad, setPad] = useState(0);
  const pauseWhileRef = useRef(options?.pauseWhile);
  pauseWhileRef.current = options?.pauseWhile;

  const sync = useCallback(() => {
    if (pauseWhileRef.current?.()) {
      return;
    }
    /**
     * 键盘顶起时 offsetTop>0：物理顶会被抬高，误判为「已在原生栏下」从而把 pad 置 0。
     * 收起时 offsetTop 往往只走 visualViewport 的 scroll，不保证再有 resize。
     * 上移期间保持上次正确 pad，待 offsetTop 归零后再重算。
     */
    if (isVisualViewportPanned()) {
      return;
    }
    const el = ref.current;
    if (!el) {
      setPad(0);
      return;
    }
    const scrollRoot =
      el.closest(".lf-h5-space-main") ??
      el.closest(".h5-sub-page__body") ??
      el.closest(".lf-h5-page-scroll") ??
      el.parentElement;
    if (scrollRoot && scrollRoot.scrollTop > 1) {
      return;
    }
    setPad(calcSafeAreaTopPadPx(el));
  }, [ref]);

  useLayoutEffect(() => {
    sync();
    const raf = requestAnimationFrame(sync);

    window.addEventListener("resize", sync);
    window.addEventListener("orientationchange", sync);
    window.visualViewport?.addEventListener("resize", sync);
    // 键盘收起时 offsetTop 复位常只触发 scroll，必须监听
    window.visualViewport?.addEventListener("scroll", sync);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", sync);
      window.removeEventListener("orientationchange", sync);
      window.visualViewport?.removeEventListener("resize", sync);
      window.visualViewport?.removeEventListener("scroll", sync);
    };
  }, [sync]);

  return pad;
}
